import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Controls from "./Controls";
import { Helmet, HelmetProvider } from "react-helmet-async";

import { Text, Circle, Group, Layer, Line, Rect, Stage } from "react-konva";
import Konva from "konva";

class Point {
  x: number;
  y: number;
  constructor(x, y) {
    this.x = x;
    this.y = y;
  }
  distance(p: Point) {
    return Math.sqrt((this.x - p.x) ** 2 + (this.y - p.y) ** 2);
  }
}

class Node {
  x: number;
  y: number;
  isEdge: boolean;
  constructor(x, y, isEedge) {
    this.x = x;
    this.y = y;
    this.isEdge = isEedge;
  }
}

class Edge {
  start: Point;
  end: Point;
  up: number;
  constructor(start, end, up) {
    this.start = start;
    this.end = end;
    this.up = up;
  }
}

class GraphEdge {
  start: Point;
  end: Point;
  constructor(start, end) {
    this.start = start;
    this.end = end;
  }
}

const initPolys = [
  [150, 150, 300, 350, 500, 180],
  [400, 400, 300, 600, 700, 600, 800, 500],
];

const point1 = new Point(150, 150);
const point2 = new Point(300, 350);
const point3 = new Point(500, 180);
const point4 = new Point(400, 400);
const point5 = new Point(300, 600);
const point6 = new Point(700, 600);
const point7 = new Point(800, 500);

const defaultHeight = 700;
const defaultWidth = 1000;

const node1 = new Node(point1.x / 2, defaultHeight / 2, false);
const node2 = new Node(
  (point1.x + point2.x) / 2,
  (defaultHeight + (point1.y + point2.y) / 2) / 2,
  false
);
const node3 = new Node(
  (point1.x + point3.x) / 2,
  (point1.y + point3.y) / 2 / 2,
  false
);
const node4 = new Node((point2.x + point4.x) / 2, 400, false);
const node5 = new Node((point3.x + point4.x) / 2, 320, false);
const node6 = new Node(
  (point5.x + point6.x) / 2,
  (defaultHeight + (point5.y + point6.y) / 2) / 2,
  false
);
const node7 = new Node((point3.x + point7.x) / 2, 250, false);
const node8 = new Node(
  (point6.x + point7.x) / 2,
  (defaultHeight + (point6.y + point7.y) / 2) / 2,
  false
);
const node9 = new Node((point7.x + defaultWidth) / 2, defaultHeight / 2, false);
const node10 = new Node(point1.x, point1.y / 2, true);
const node11 = new Node(point1.x, (point1.y + defaultHeight) / 2, true);
const node12 = new Node(point2.x, (point2.y + point5.y) / 2, true);
const node13 = new Node(point4.x, 330, true);
const node14 = new Node(point3.x, point3.y / 2, true);
const node15 = new Node(point3.x, 350, true);
const node16 = new Node(point7.x, point7.y / 2, true);
const node17 = new Node(point7.x, (point7.y + defaultHeight) / 2, true);
const node18 = new Node(point5.x, (point5.y + defaultHeight) / 2, true);
const node19 = new Node(point6.x, (point6.y + defaultHeight) / 2, true);

function Main() {
  const [points, setPoints] = useState([
    point1,
    point2,
    point3,
    point4,
    point5,
    point6,
    point7,
  ]);
  const [polys, setPolys] = useState(initPolys);
  const [edges, setEdges] = useState<Edge[]>([
    new Edge(point1, new Point(point1.x, 0), true),
    new Edge(point1, new Point(point1.x, defaultHeight), false),
    new Edge(point3, new Point(point3.x, 0), true),
    new Edge(point3, new Point(point3.x, 425), false),
    new Edge(point4, new Point(point4.x, 265), true),
    new Edge(point2, point5, false),
    new Edge(point5, new Point(point5.x, defaultHeight), false),
    new Edge(point6, new Point(point6.x, defaultHeight), false),
    new Edge(point7, new Point(point7.x, 0), true),
    new Edge(point7, new Point(point7.x, defaultHeight), false),
  ]);
  const [vertices, setVertices] = useState<Node[]>([
    node1,
    node2,
    node3,
    node4,
    node5,
    node6,
    node7,
    node8,
    node9,
    node10,
    node11,
    node12,
    node13,
    node18,
    node19,
    node14,
    node15,
    node16,
    node17,
  ]);
  const [graphEdges, setGraphEdges] = useState<GraphEdge[]>([
    new GraphEdge(node1, node10),
    new GraphEdge(node3, node10),
    new GraphEdge(node3, node14),
    new GraphEdge(node1, node11),
    new GraphEdge(node2, node11),
    new GraphEdge(node2, node12),
    new GraphEdge(node4, node12),
    new GraphEdge(node4, node13),
    new GraphEdge(node5, node13),
    new GraphEdge(node5, node15),
    new GraphEdge(node2, node18),
    new GraphEdge(node6, node18),
    new GraphEdge(node6, node19),
    new GraphEdge(node8, node19),
    new GraphEdge(node8, node17),
    new GraphEdge(node9, node17),
    new GraphEdge(node9, node16),
    new GraphEdge(node7, node16),
    new GraphEdge(node7, node15),
    new GraphEdge(node7, node14),
  ]);
  const [hidePoints, setHidePoints] = useState(false);
  const divRef = useRef(null);

  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
  });
  const [scaleX, setScaleX] = useState(1);
  const [scaleY, setScaleY] = useState(1);
  const groupRef: React.RefObject<Konva.Group> = useRef();

  useEffect(() => {
    setScaleX(dimensions.width / defaultWidth);
    setScaleY(dimensions.height / defaultHeight);
  }, [dimensions]);

  const restart = () => {
    setPolys(initPolys);
    setPoints([]);
    setEdges([]);
    setVertices([]);
    setHidePoints(false);
  };

  useEffect(() => {
    if (divRef.current?.offsetHeight && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetHeight,
      });
    }
    if (divRef.current?.offsetHeight == 0 && divRef.current?.offsetWidth) {
      setDimensions({
        width: divRef.current.offsetWidth,
        height: divRef.current.offsetWidth,
      });
    }
  }, [divRef]);

  const [step, setStep] = useState(0);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", sm: "column", md: "row" },
        flexGrow: 1,
      }}
    >
      <Box sx={{ flex: 7 }}>
        <Controls step={step} setStep={setStep} />
      </Box>
      <Box
        sx={{
          flex: 13,
          minHeight: 600,
          backgroundColor: "#efefef",
          position: "relative",
          height: "100%",
        }}
        ref={divRef}
      >
        <Box
          sx={{
            position: "absolute",
            display: "flex",
            flexDirection: "row",
            width: "100%",
            justifyContent: "center",
            margin: 2,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginRight: 5,
            }}
          ></Box>
        </Box>
        <Stage width={dimensions.width} height={dimensions.height}>
          <Layer>
            <Group ref={groupRef}>
              {step > 1 &&
                edges
                  .filter((e) => e.up)
                  .map((e, i) => {
                    return (
                      <Line
                        key={i}
                        strokeWidth={5}
                        stroke={e.up ? "red" : "blue"}
                        points={[
                          e.start.x * scaleX,
                          e.start.y * scaleY,
                          e.end.x * scaleX,
                          e.end.y * scaleY,
                        ]}
                      />
                    );
                  })}
              {step > 2 &&
                edges
                  .filter((e) => !e.up)
                  .map((e, i) => {
                    return (
                      <Line
                        key={i}
                        strokeWidth={5}
                        stroke={e.up ? "red" : "blue"}
                        points={[
                          e.start.x * scaleX,
                          e.start.y * scaleY,
                          e.end.x * scaleX,
                          e.end.y * scaleY,
                        ]}
                      />
                    );
                  })}
              {step > 5 &&
                graphEdges.map((e, i) => {
                  return (
                    <Line
                      key={i}
                      strokeWidth={5}
                      stroke={"black"}
                      points={[
                        e.start.x * scaleX,
                        e.start.y * scaleY,
                        e.end.x * scaleX,
                        e.end.y * scaleY,
                      ]}
                    />
                  );
                })}
              {step > 3 &&
                vertices
                  .filter((v) => (step == 4 ? !v.isEdge : true))
                  .map((p, i) => {
                    return (
                      <Group key={"point" + i}>
                        <Circle
                          radius={20 * scaleX}
                          strokeWidth={5}
                          stroke={"black"}
                          fill="white"
                          x={p.x * scaleX}
                          y={p.y * scaleY}
                        />
                        <Text
                          fontSize={20}
                          align="center"
                          verticalAlign="middle"
                          fill={"black"}
                          fillAfterStrokeEnabled={true}
                          width={40}
                          height={40}
                          x={p.x * scaleX - 20}
                          y={p.y * scaleY - 20}
                          text={(i + 10).toString(36).toUpperCase()}
                        />
                      </Group>
                    );
                  })}
              {step > 0 &&
                points.map((p, i) => (
                  <Circle
                    key={i}
                    radius={10}
                    fill="orange"
                    x={p.x * scaleX}
                    y={p.y * scaleY}
                  />
                ))}
              {polys.map((p, i) => (
                <Line
                  points={p.map((v, i) => (i % 2 ? v * scaleY : v * scaleX))}
                  key={i}
                  strokeWidth={5}
                  fill={"orange"}
                  closed
                  stroke={"orange"}
                />
              ))}
            </Group>
          </Layer>
        </Stage>
      </Box>
    </Box>
  );
}

interface TabPanelProps {
  children?: React.ReactNode;
}

function CellDecomposition(props: TabPanelProps) {
  const { children, ...other } = props;

  return (
    <HelmetProvider>
      <Main />
      <Helmet>
        <title>Visualization of Cell Decomposition</title>
        <meta name="description" content="" />
      </Helmet>
    </HelmetProvider>
  );
}

export default CellDecomposition;
